/// <amd-module name="Test/TestPlansHubRefresh/TestCasesClonePanel/Redux/TestCasesClonePanelSaga" />
import { effects, SagaIterator } from "redux-saga";
import { ActionsOfType } from "redux/Utils/ActionHelper";
import { ITestHubDataService } from "Test/TestPlansHubRefresh/Service/TestHubDataService";
import { TestHubRefreshConstants } from "Test/TestPlansHubRefresh/TestHub/Constants";
import { AuthorActions } from "Test/TestPlansHubRefresh/Pivots/AuthorPivot/Redux/AuthorActions";
import { CloneTestCaseOperationInformation, TestPlan, TestSuite } from "TestPlanCommon/Contracts/TestPlan";
import { TestArtifactWithContinuationToken } from "TestPlanCommon/Contracts/TestPlanExtended";
import { TeamProjectReference } from "Tfs/Platform/Contracts/Core";
import { IVssPageContext } from "VSS/Platform/Context";
import { TestCasesClonePanelActions, TestCasesClonePanelActionsType } from "./TestCasesClonePanelActions";
import { SuitesActions } from "Test/TestPlansHubRefresh/TestHub/Redux/Suites/SuitesActions";
import * as Resources from "Test/TestPlansHubRefresh/TestCasesClonePanel/Resources";
import { errorHandledSaga } from "Test/TestPlansHubRefresh/TestHub/Util";
import { TelemetryConstants } from "Test/TestPlansHubRefresh/TestHub/Constants";
import { TelemetryActions } from "Test/TestPlansHubRefresh/TestHub/Redux/TelemetryActions";
import { isAdvancedLicenseEnabled } from "Test/TestPlansHubRefresh/TestHub/Redux/TestHubRefresh/TestHubRefreshSelector";

export function* TestCasesClonePanelSaga(): SagaIterator {
    yield effects.takeLatest(TestCasesClonePanelActionsType.copyTestCases, _copyTestCases);
    yield effects.takeLatest(TestCasesClonePanelActionsType.getProjectsFromServer, _getProjectsFromServer);
    yield effects.takeLatest(TestCasesClonePanelActionsType.getTestPlans, _getTestPlans);
    yield effects.takeLatest(TestCasesClonePanelActionsType.getTestSuitesForPlan, _getTestSuitesForPlan);
}

export function* copyTestCases(action: ActionsOfType<TestCasesClonePanelActions, TestCasesClonePanelActionsType.copyTestCases>): SagaIterator {
    try {
        yield effects.put(TestCasesClonePanelActions.copyTestCasesInProgress());
        const pageContext: IVssPageContext = (yield effects.getContext("pageContext")) as any;

        const dataService: ITestHubDataService = (yield effects.call([pageContext, pageContext.getService], "ITestHubDataService")) as any;
        let cloneTestCaseOperationInformation: CloneTestCaseOperationInformation = (yield effects.call(
            [dataService, dataService.cloneTestCases],
            action.payload.sourcePlanId,
            action.payload.sourcePlanName,
            action.payload.destinationPlanId,
            action.payload.destinationPlanName,
            action.payload.sourceSuiteId,
            action.payload.destinationSuiteId,
            action.payload.sourceProjectName,
            action.payload.destinationProjectName,
            action.payload.testCaseIds,
            action.payload.includeLinks,
            action.payload.includeAttachments
        )) as any;
        let opId: number = cloneTestCaseOperationInformation.cloneOperationResponse
            ? cloneTestCaseOperationInformation.cloneOperationResponse.opId
            : -1;
        if (opId > 0) {
            yield effects.put(
                AuthorActions.getTestCasesCloneOpState(
                    action.payload.sourcePlanId,
                    action.payload.sourceSuiteId,
                    action.payload.destinationPlanId,
                    action.payload.destinationSuiteId,
                    action.payload.destinationProjectName,
                    opId,
                    true,
                    action.payload.sourceProjectName
                )
            );
            const telemetryPayload = {
                sourceSuiteId: action.payload.sourceSuiteId,
                testCaseIds: action.payload.testCaseIds,
                destinationSuiteId: action.payload.destinationSuiteId,
                destinationPlanId: action.payload.destinationPlanId,
                isAdvancedLicenseEnabled: yield effects.select(isAdvancedLicenseEnabled)
            };
            yield effects.put(TelemetryActions.publishTelemetry(TelemetryConstants.CLONE_TEST_CASES, telemetryPayload));
            yield effects.put(TestCasesClonePanelActions.copyTestCasesCompleted(opId));
        } else {
            yield effects.put(SuitesActions.showToastNotifications(Resources.FailedOperationError, TestHubRefreshConstants.TOAST_MESSAGE_DURATION));
            yield effects.put(TestCasesClonePanelActions.copyTestCasesCompleted(0));
        }
    } catch (e) {
        yield effects.put(SuitesActions.showToastNotifications(e.message, TestHubRefreshConstants.TOAST_MESSAGE_DURATION));
        yield effects.put(TestCasesClonePanelActions.copyTestCasesCompleted(0));
    }
}

export function* getProjectsFromServer(
    action: ActionsOfType<TestCasesClonePanelActions, TestCasesClonePanelActionsType.getProjectsFromServer>
): SagaIterator {
    const pageContext: IVssPageContext = (yield effects.getContext("pageContext")) as any;
    const dataService: ITestHubDataService = (yield effects.call([pageContext, pageContext.getService], "ITestHubDataService")) as any;

    const projectList: TeamProjectReference[] = (yield effects.call([dataService, dataService.getProjects])) as any;

    yield effects.put(TestCasesClonePanelActions.projectsFetchSucceeded(projectList));
}

export function* getTestPlans(action: ActionsOfType<TestCasesClonePanelActions, TestCasesClonePanelActionsType.getTestPlans>): SagaIterator {
    const pageContext: IVssPageContext = (yield effects.getContext("pageContext")) as any;
    const dataService: ITestHubDataService = (yield effects.call([pageContext, pageContext.getService], "ITestHubDataService")) as any;
    const project: string = action.payload.projectName;

    let continuationToken = action.payload.continuationToken;

    const planListWithToken: TestArtifactWithContinuationToken<TestPlan> = (yield effects.call(
        [dataService, dataService.getTestPlans],
        project,
        undefined,
        continuationToken
    )) as any;

    yield effects.put(TestCasesClonePanelActions.testPlansFetchSucceeded(project, planListWithToken.results));

    const pageSize: number = TestHubRefreshConstants.FETCH_TESTPLANS_PAGE_SIZE;
    if (planListWithToken.continuationToken) {
        const tokenSplits: string[] = planListWithToken.continuationToken.split(";");
        if (tokenSplits && tokenSplits.length == 2) {
            tokenSplits[1] = pageSize.toString();
            planListWithToken.continuationToken = tokenSplits.join(";");
        }
        yield effects.put(TestCasesClonePanelActions.getTestPlans(project, planListWithToken.continuationToken));
    }
}

export function* getTestSuitesForPlan(
    action: ActionsOfType<TestCasesClonePanelActions, TestCasesClonePanelActionsType.getTestSuitesForPlan>
): SagaIterator {
    try {
        const pageContext: IVssPageContext = (yield effects.getContext("pageContext")) as any;
        const dataService: ITestHubDataService = (yield effects.call([pageContext, pageContext.getService], "ITestHubDataService")) as any;

        const suitesList: TestSuite[] = (yield effects.call(
            [dataService, dataService.getTestSuitesForPlan],
            action.payload.planId,
            undefined,
            undefined,
            true,
            action.payload.project
        )) as any;

        yield effects.put(TestCasesClonePanelActions.getTestSuitesForPlanSucceeded(suitesList));
    } catch (error) {
        const telemetryPayload = {
            planId: action.payload.planId,
            errorMessage: error.message
        }
        yield effects.put(TelemetryActions.publishTelemetry(TelemetryConstants.COPYTESTCASES_TESTPLAN_SELECTION_ERROR, telemetryPayload));
        yield effects.put(TestCasesClonePanelActions.testCasesClonePanelError(error.message));
        yield effects.put(TestCasesClonePanelActions.disableTestSuiteEditableDropdownSelection(true));
    }
}

const _copyTestCases = errorHandledSaga(function* (
    action: ActionsOfType<TestCasesClonePanelActions, TestCasesClonePanelActionsType.copyTestCases>
): SagaIterator {
    yield effects.call(copyTestCases, action);
});

const _getProjectsFromServer = errorHandledSaga(function* (
    action: ActionsOfType<TestCasesClonePanelActions, TestCasesClonePanelActionsType.getProjectsFromServer>
): SagaIterator {
    yield effects.call(getProjectsFromServer, action);
});

const _getTestPlans = errorHandledSaga(function* (
    action: ActionsOfType<TestCasesClonePanelActions, TestCasesClonePanelActionsType.getTestPlans>
): SagaIterator {
    yield effects.call(getTestPlans, action);
});

const _getTestSuitesForPlan = errorHandledSaga(function* (
    action: ActionsOfType<TestCasesClonePanelActions, TestCasesClonePanelActionsType.getTestSuitesForPlan>
): SagaIterator {
    yield effects.call(getTestSuitesForPlan, action);
});
